import { useEffect, useMemo, useRef, useState } from 'react'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { getPiggyBankMonthly, PiggyBankMonth, PiggyBankUser } from '../../data/piggyBankData'
import { useToast } from '../../contexts/ToastContext'
import { getApiErrorDetails } from '../../helpers/apiError'

// Categorical slots 1 and 2 of the reference palette, validated together on
// the light surface (CVD deltaE 24.7, both >= 3:1). The portal's navy/green
// pair fails the lightness and chroma checks for a two-series stack, so it
// isn't used here. Identity follows the series, never its size.
const COLOUR_REFERRALS = '#2a78d6'
const COLOUR_ITEMS = '#eb6834'

// Recessive ink for axes and legend text - never a series colour.
const INK_MUTED = '#5a6472'
const GRID = '#e9ecef'
const SURFACE = '#ffffff'

const PERIODS = [
  { text: 'Last 12 months', value: 12 },
  { text: 'Last 24 months', value: 24 },
  { text: 'Last 36 months', value: 36 },
]

interface PersonOption {
  text: string
  value: number | null
}

const ALL_PEOPLE: PersonOption = { text: 'All people', value: null }

const money = (value: number) =>
  `£${(Number(value) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const compactMoney = (value: number) => {
  const abs = Math.abs(Number(value) || 0)
  if (abs >= 1_000_000) return `£${(abs / 1_000_000).toFixed(1)}m`
  if (abs >= 1_000) return `£${(abs / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`
  return `£${Math.round(abs)}`
}

const monthLabel = (m: PiggyBankMonth, long = false) =>
  new Date(m.year, m.month - 1, 1).toLocaleDateString('en-GB', long ? { month: 'long', year: 'numeric' } : { month: 'short', year: '2-digit' })

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`

const legendText = (value: string) => <span style={{ color: INK_MUTED, fontSize: '9pt' }}>{value}</span>

/** One month's breakdown on hover. Text stays in ink; the swatches carry identity. */
const MonthTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const m: PiggyBankMonth = payload[0].payload
  return (
    <div className='pb-tip'>
      <div className='pb-tip__title'>{monthLabel(m, true)}</div>
      <div className='pb-tip__row'>
        <span className='pb-tip__swatch' style={{ background: COLOUR_REFERRALS }} />
        <span>Referrals ({m.referralCount})</span>
        <span className='pb-tip__value'>{money(m.referralTotal)}</span>
      </div>
      <div className='pb-tip__row'>
        <span className='pb-tip__swatch' style={{ background: COLOUR_ITEMS }} />
        <span>Items ({m.allocationCount})</span>
        <span className='pb-tip__value'>{money(m.allocationTotal)}</span>
      </div>
      <div className='pb-tip__row pb-tip__row--total'>
        <span />
        <span>Total</span>
        <span className='pb-tip__value'>{money(m.total)}</span>
      </div>
    </div>
  )
}

/**
 * Month on month, what went into piggy banks: everyone's, or one person's.
 * Each column is that month's referrals with the items given stacked on top.
 */
const PiggyBankTrends = ({ users }: { users: PiggyBankUser[] }) => {
  const { showToast } = useToast()
  const [person, setPerson] = useState<PersonOption>(ALL_PEOPLE)
  const [months, setMonths] = useState(PERIODS[0])
  const [data, setData] = useState<PiggyBankMonth[] | null>(null)
  const [loading, setLoading] = useState(false)

  // Picking quickly leaves several requests in flight; only the newest lands,
  // and the previous chart stays up meanwhile rather than flashing empty.
  const latest = useRef(0)

  useEffect(() => {
    const requestId = ++latest.current
    setLoading(true)
    getPiggyBankMonthly(months.value, person.value)
      .then((rows) => {
        if (requestId === latest.current) setData(rows)
      })
      .catch((error) => {
        if (requestId === latest.current) {
          showToast(getApiErrorDetails(error, "The monthly figures couldn't be loaded.").message, 'error')
        }
      })
      .finally(() => {
        if (requestId === latest.current) setLoading(false)
      })
  }, [months, person, showToast])

  const people: PersonOption[] = useMemo(
    () => [
      ALL_PEOPLE,
      ...[...users]
        .sort((a, b) => a.employeeName.localeCompare(b.employeeName))
        .map((u) => ({ text: u.isDeleted ? `${u.employeeName} (deleted)` : u.employeeName, value: u.employeeId })),
    ],
    [users]
  )

  const rows = data ?? []
  const total = rows.reduce((sum, m) => sum + m.total, 0)
  const average = rows.length ? total / rows.length : 0
  const thisMonth = rows.length ? rows[rows.length - 1] : null
  const empty = rows.length > 0 && rows.every((m) => m.total === 0)
  const chartRows = rows.map((m) => ({ ...m, label: monthLabel(m) }))

  return (
    <div>
      {/* Filters in one row above everything they drive. */}
      <div className='pb-toolbar'>
        <div className='pb-filters'>
          <DropDownList
            data={people}
            textField='text'
            dataItemKey='value'
            value={person}
            onChange={(e) => setPerson(e.value ?? ALL_PEOPLE)}
            filterable={people.length > 10}
            style={{ width: '240px' }}
            aria-label='Person'
          />
          <DropDownList
            data={PERIODS}
            textField='text'
            dataItemKey='value'
            value={months}
            onChange={(e) => setMonths(e.value)}
            style={{ width: '170px' }}
            aria-label='Period'
          />
        </div>
        {loading && data && <span className='pb-toolbar__total'>Updating…</span>}
      </div>

      {!data ? (
        <p className='lk-muted'>Loading…</p>
      ) : (
        <>
          <div className='pb-kpis'>
            <div className='pb-kpi'>
              <div className='pb-kpi__label'>Total, {months.text.toLowerCase()}</div>
              <div className='pb-kpi__value'>{money(total)}</div>
            </div>
            <div className='pb-kpi'>
              <div className='pb-kpi__label'>Average a month</div>
              <div className='pb-kpi__value'>{money(average)}</div>
            </div>
            <div className='pb-kpi'>
              <div className='pb-kpi__label'>{thisMonth ? `${monthLabel(thisMonth, true)} so far` : 'This month'}</div>
              <div className='pb-kpi__value'>{money(thisMonth?.total ?? 0)}</div>
            </div>
          </div>

          <section className='lk-card pb-chart'>
            <h2 className='lk-subtitle' style={{ marginTop: 0 }}>
              Month on month - {person.value === null ? 'all people' : person.text}
            </h2>
            {empty ? (
              <p className='lk-muted'>Nothing went into {person.value === null ? 'anyone’s' : 'their'} piggy bank in this period.</p>
            ) : (
              <ResponsiveContainer width='100%' height={320}>
                <BarChart data={chartRows} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis
                    dataKey='label'
                    tick={{ fontSize: '9pt', fill: INK_MUTED }}
                    tickLine={false}
                    axisLine={{ stroke: GRID }}
                    interval={months.value > 12 ? 'preserveStartEnd' : 0}
                    minTickGap={8}
                  />
                  <YAxis
                    tickFormatter={compactMoney}
                    tick={{ fontSize: '9pt', fill: INK_MUTED }}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                    allowDecimals={false}
                  />
                  <Tooltip content={<MonthTooltip />} cursor={{ fill: 'rgba(30, 42, 78, 0.06)' }} />
                  <Legend verticalAlign='top' align='right' height={28} formatter={legendText} />
                  {/* Referrals sit on the baseline, items on top; the white
                      stroke is the 2px surface gap between the two. */}
                  <Bar
                    dataKey='referralTotal'
                    name='Referrals'
                    stackId='bank'
                    fill={COLOUR_REFERRALS}
                    stroke={SURFACE}
                    strokeWidth={2}
                    maxBarSize={24}
                  />
                  <Bar
                    dataKey='allocationTotal'
                    name='Items'
                    stackId='bank'
                    fill={COLOUR_ITEMS}
                    stroke={SURFACE}
                    strokeWidth={2}
                    maxBarSize={24}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </section>

          {/* The same figures as a table, so no value is hover-only. */}
          <section className='lk-card'>
            <h2 className='lk-subtitle' style={{ marginTop: 0 }}>
              By month
            </h2>
            <table className='pb-table'>
              <thead>
                <tr>
                  <th>Month</th>
                  <th className='pb-num'>Referrals</th>
                  <th className='pb-num'>Items</th>
                  <th className='pb-num'>Total</th>
                </tr>
              </thead>
              <tbody>
                {[...rows].reverse().map((m) => (
                  <tr key={`${m.year}-${m.month}`}>
                    <td>{monthLabel(m, true)}</td>
                    <td className='pb-num'>
                      {money(m.referralTotal)}
                      <div className='pb-sub'>{plural(m.referralCount, 'referral')}</div>
                    </td>
                    <td className='pb-num'>
                      {money(m.allocationTotal)}
                      <div className='pb-sub'>{plural(m.allocationCount, 'item')}</div>
                    </td>
                    <td className='pb-num pb-strong'>{money(m.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  <td className='pb-num'>{money(rows.reduce((s, m) => s + m.referralTotal, 0))}</td>
                  <td className='pb-num'>{money(rows.reduce((s, m) => s + m.allocationTotal, 0))}</td>
                  <td className='pb-num pb-strong'>{money(total)}</td>
                </tr>
              </tfoot>
            </table>
          </section>
        </>
      )}
    </div>
  )
}

export default PiggyBankTrends
